import React, { ReactNode } from "react";

import {
  IgAzurePrimitives,
  IgBasePrimitives,
  IgBluePrimitives,
  IgCoralPrimitives,
  IgGrayPrimitives,
  IgRedPrimitives,
} from "@intelligems/ig-design-tokens/constants";
import { IgIconButton } from "@intelligems/ig-theme/IgIconButton";
import { IgTypography } from "@intelligems/ig-theme/IgTypography";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";

type BannerVariant = "info" | "alert" | "secondary";

interface IgAppBannerProps {
  message: ReactNode | string;
  title: ReactNode | string;
  buttons?: ReactNode[];
  hideDismiss?: boolean;
  dismissId?: string;
  variant?: BannerVariant;
  /** Content to render at the far right of the banner (e.g., expand chevron) */
  endContent?: ReactNode;
}

const VARIANT_STYLES = {
  info: {
    foreground: "white",
    border: `1px solid ${IgBluePrimitives["A300"]}`,
    background: `linear-gradient(48deg, ${IgAzurePrimitives["A100"]} 0%, ${IgBasePrimitives[500]} 47.3%, ${IgBasePrimitives["A200"]} 94.59%)`,
  },
  alert: {
    foreground: "white",
    border: `1px solid ${IgCoralPrimitives[900]}`,
    background: `linear-gradient(48deg, ${IgCoralPrimitives[700]} 0%, ${IgCoralPrimitives[800]} 47.3%, ${IgRedPrimitives["A300"]} 94.59%)`,
  },
  secondary: {
    foreground: "black",
    border: `1px solid ${IgGrayPrimitives[800]}`,
    background: `linear-gradient(48deg, ${IgGrayPrimitives[300]} 0%, ${IgGrayPrimitives[300]} 47.3%, ${IgGrayPrimitives[200]} 94.59%)`,
  },
} as const;

export const IgAppBanner = ({
  message,
  title,
  buttons,
  hideDismiss = true,
  dismissId,
  variant = "info",
  endContent,
}: IgAppBannerProps) => {
  const styles = VARIANT_STYLES[variant];

  return (
    <Stack
      direction="row"
      sx={{
        width: "100%",
        minHeight: 48,
        py: 1.5,
        px: 2,
        alignItems: "center",
        background: styles.background,
        color: styles.foreground,
        borderBottom: styles.border,
        justifyContent: "center",
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="center"
        spacing={{ xs: 2, md: 8 }}
        sx={{ minWidth: 0, px: 3, rowGap: 1 }}
      >
        <Box
          sx={{
            textAlign: "center",
            minWidth: 200,
            maxWidth: "100ch",
            wordBreak: "normal",
          }}
        >
          <IgTypography variant="body1" sx={{ color: styles.foreground }}>
            <strong>{title}</strong> {message}
          </IgTypography>
        </Box>

        {buttons && buttons.length > 0 && (
          <Stack direction="row" spacing={2} sx={{ flexShrink: 0 }}>
            {buttons}
          </Stack>
        )}

        {!hideDismiss && dismissId && (
          <IgIconButton
            iconType="close"
            onClick={() => {
              window.dispatchEvent(
                new CustomEvent("banner-dismiss", { detail: { dismissId } })
              );
            }}
          />
        )}
      </Stack>
      {endContent && <Box sx={{ flexShrink: 0, ml: 2 }}>{endContent}</Box>}
    </Stack>
  );
};
